import { FC, useCallback, useMemo } from 'react';
import { renderToString } from 'react-dom/server';
import { InterviewStatus, Profile, ProfileForAdmin, User, UserMessage } from '../../../common/Types';
import {
  RemoteProcedureCall,
  useApplicationStateLoader,
  useApplicationStateValue,
} from '@resistdesign/voltra/app/utils';
import { APP_ENV_CONFIG } from '../../config';
import { ADMIN } from '../../../api/AppRoutes/AdminRoutes/IDs';
import { SYM } from '../../Components/MaterialSymbol';
import styled from 'styled-components';
import marked from 'marked';
import { jsPDF } from 'jspdf';

const PASSING_RATING = 75;

const { parse: parseMarkdown } = marked;

const InterviewStatusColorMap: Record<InterviewStatus, string> = {
  [InterviewStatus.INTERVIEW_NOT_STARTED]: 'gray',
  [InterviewStatus.INTERVIEW_STARTED]: 'yellow',
  [InterviewStatus.LAST_QUESTION_STARTED]: 'yellow-green',
  [InterviewStatus.LAST_QUESTION_ANSWERED]: 'green-yellow',
  [InterviewStatus.INTERVIEW_COMPLETED]: 'green',
  [InterviewStatus.INTERVIEW_EXPIRED]: 'red',
};
const InterviewStatusIconMap: Record<InterviewStatus, string> = {
  [InterviewStatus.INTERVIEW_NOT_STARTED]: 'schedule',
  [InterviewStatus.INTERVIEW_STARTED]: 'play_circle',
  [InterviewStatus.LAST_QUESTION_STARTED]: 'question_answer',
  [InterviewStatus.LAST_QUESTION_ANSWERED]: 'checklist',
  [InterviewStatus.INTERVIEW_COMPLETED]: 'check_circle',
  [InterviewStatus.INTERVIEW_EXPIRED]: 'timer_off',
};
const InterviewStatusLabelMap: Record<InterviewStatus, string> = {
  [InterviewStatus.INTERVIEW_NOT_STARTED]: 'Interview not started',
  [InterviewStatus.INTERVIEW_STARTED]: 'Interview in progress',
  [InterviewStatus.LAST_QUESTION_STARTED]: 'Last question started',
  [InterviewStatus.LAST_QUESTION_ANSWERED]: 'Last question answered',
  [InterviewStatus.INTERVIEW_COMPLETED]: 'Interview completed',
  [InterviewStatus.INTERVIEW_EXPIRED]: 'Interview expired',
};

const {
  api: { domain: API_DOMAIN },
} = APP_ENV_CONFIG;

const downloadInterviewTranscript = (user: User, profile: Profile, interviewTranscript: UserMessage[] = []) => {
  const { given_name, family_name, email, phone_number } = user;
  const { linkedInUrl, city } = profile;
  const iTMarkup = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'sans-serif',
        fontSize: '3pt',
        color: 'black',
      }}
    >
      <header>
        <h6>
          {given_name} {family_name}
        </h6>
        <div>
          <strong>City:</strong> {city}
        </div>
        <div>
          <a href={`mailto:${email}`} target="_blank" rel="noreferrer">
            <strong>Email:</strong> {email}
          </a>
        </div>
        <div>
          <a href={`tel:${phone_number}`} target="_blank" rel="noreferrer">
            <strong>Phone:</strong> {phone_number}
          </a>
        </div>
        <div>
          <a href={linkedInUrl} target="_blank" rel="noreferrer">
            <strong>LinkedIn:</strong> {linkedInUrl}
          </a>
        </div>
      </header>
      <br />
      {interviewTranscript.map(({ role, content = '' }) => (
        <div>
          <strong>{role === 'assistant' ? 'Interviewer:' : 'Candidate:'}</strong>
          <div
            dangerouslySetInnerHTML={{
              __html: parseMarkdown(content),
            }}
          ></div>
        </div>
      ))}
    </div>
  );
  const iTString = renderToString(iTMarkup);
  const pdf = new jsPDF();

  pdf.html(iTString, {
    autoPaging: true,
    windowWidth: 210,
    width: 180,
    margin: 10,
    callback: () => pdf.save('interview-transcript.pdf'),
  });
};

const DetailList = styled.dl`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 1em;
`;
const StatusLabel = styled.div`
  flex: 1 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5em;
`;
const getStatusLabel = (status: InterviewStatus) => (
  <StatusLabel
    style={{
      color: InterviewStatusColorMap[status],
    }}
  >
    <SYM>{InterviewStatusIconMap[status]}</SYM>
    {InterviewStatusLabelMap[status]}
  </StatusLabel>
);

export type ProfileDetailsProps = {
  authorization: string;
  user: User;
  profileId: string;
};

export const ProfileDetails: FC<ProfileDetailsProps> = ({ authorization, user, profileId }) => {
  const PROFILE_FOR_ADMIN_RPC = useMemo<RemoteProcedureCall>(
    () => ({
      serviceConfig: {
        protocol: 'https',
        domain: API_DOMAIN,
        basePath: '/admin',
        authorization,
      },
      path: '/user-profile',
      args: [profileId],
    }),
    [authorization, profileId]
  );
  const INTERVIEW_TRANSCRIPT_RPC = useMemo<RemoteProcedureCall>(
    () => ({
      serviceConfig: {
        protocol: 'https',
        domain: API_DOMAIN,
        basePath: '/admin',
        authorization,
      },
      path: '/interview-transcript',
      args: [profileId],
    }),
    [authorization, profileId]
  );
  const { value: profileValue } = useApplicationStateValue(ADMIN.USER_PROFILE);
  const { value: interviewTranscriptValue = [] } = useApplicationStateValue(ADMIN.INTERVIEW_TRANSCRIPT);
  const {
    status = InterviewStatus.INTERVIEW_NOT_STARTED,
    rating = 0,
    linkedInUrl = '',
    city = '',
    audioSampleDownloadUrl = '',
    resumeDownloadUrl = '',
  }: Partial<ProfileForAdmin> = profileValue || {};
  const { loading: profileLoading = false } = useApplicationStateLoader({
    identifier: ADMIN.USER_PROFILE,
    remoteProcedureCall: PROFILE_FOR_ADMIN_RPC,
  });
  const { loading: interviewTranscriptLoading = false } = useApplicationStateLoader({
    identifier: ADMIN.INTERVIEW_TRANSCRIPT,
    remoteProcedureCall: INTERVIEW_TRANSCRIPT_RPC,
  });
  const isLoading = profileLoading || interviewTranscriptLoading;
  const onOpenInterviewTranscript = useCallback(() => {
    downloadInterviewTranscript(user, profileValue, interviewTranscriptValue);
  }, [user, profileValue, interviewTranscriptValue]);

  return (
    <div>
      {isLoading ? (
        <article aria-busy="true"></article>
      ) : (
        <DetailList>
          <dt>Interview Status</dt>
          <dd>{getStatusLabel(status)}</dd>
          {status === InterviewStatus.INTERVIEW_COMPLETED ? (
            <>
              <dt>Interview Rating</dt>
              <dd>
                <span
                  style={{
                    backgroundColor:
                      rating >= PASSING_RATING ? 'green' : rating >= 60 ? 'yellow' : rating >= 40 ? 'orange' : 'red',
                    color:
                      rating >= PASSING_RATING ? 'white' : rating >= 60 ? 'black' : rating >= 40 ? 'white' : 'white',
                    padding: '0.25em',
                    borderRadius: '0.25em',
                  }}
                >
                  {rating}% &nbsp;
                  {rating >= PASSING_RATING ? 'Pass' : 'Fail'}
                </span>
              </dd>
            </>
          ) : undefined}
          {city ? (
            <>
              <dt>City</dt>
              <dd>{city}</dd>
            </>
          ) : undefined}
          {linkedInUrl ? (
            <>
              <dt>LinkedIn URL</dt>
              <dd>
                <a href={linkedInUrl} target="_blank" rel="noreferrer">
                  {linkedInUrl}
                </a>
              </dd>
            </>
          ) : undefined}
          {resumeDownloadUrl ? (
            <>
              <dt>Resume</dt>
              <dd>
                <a href={resumeDownloadUrl} target="_blank" download>
                  Download
                </a>
              </dd>
            </>
          ) : undefined}
          {audioSampleDownloadUrl ? (
            <>
              <dt>Audio Sample</dt>
              <dd>
                <a href={audioSampleDownloadUrl} target="_blank" download>
                  Download
                </a>
              </dd>
            </>
          ) : undefined}
          {interviewTranscriptValue && interviewTranscriptValue.length > 0 ? (
            <>
              <dt>Interview Transcript</dt>
              <dd>
                <a
                  onClick={onOpenInterviewTranscript}
                  style={{
                    cursor: 'pointer',
                  }}
                >
                  Download
                </a>
              </dd>
            </>
          ) : undefined}
        </DetailList>
      )}
    </div>
  );
};
